import {formatDate} from '@angular/common';
import {Router} from '@angular/router';
import {isEmpty, isNil, isNumber, isString} from 'lodash';
import {MONTHLIST} from '../../views/wrapper/payroll/payroll-step/payroll';
import { customAlphabet } from 'nanoid';
import { PvdTypePercent } from 'src/app/views/wrapper/payroll/employee/employee';

export const MinDateTime = -62132918396000

export function getUrlWithoutParams(router: Router): string {
  const urlTree = router.parseUrl(router.url);
  urlTree.queryParams = {};
  return urlTree.toString();
}

export const IsEmptyValue = (item): boolean => {
  return (isNil(item) || (isNumber(item) && isNaN(item)) || (isString(item) && isEmpty(item.trim())));
};

export function getDisplayMonthTH(month: number): string {
  return MONTHLIST[month];
}

export const formatPhone = (phone: string): string => {
  if (!phone) {
    return '-';
  }

  const phoneN = (phone.toString()).replace(/\D/g, '');
  if (phoneN.length === 10) {
    return phoneN.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
  } else if (phoneN.length === 9) {
    return phoneN.replace(/(\d{2})(\d{3})(\d{4})/, '$1-$2-$3');
  }
  return phone;
};

export const formatBankId = (id: string): string => {
  if (!id) {
    return '-';
  }

  const idN = (id.toString()).replace(/\D/g, '');
  if (idN.length === 10) {
    return idN.replace(/(\d{3})(\d{1})(\d{5})(\d{1})/, '$1-$2-$3-$4');
  }
  return id;
};

export const formatNationalId = (id: string): string => {
  if (!id || IsEmptyValue(id)) {
    return '-';
  }

  const idN = (id.toString()).replace(/\D/g, '');
  if (idN.length === 13) {
    return idN.replace(/(\d{1})(\d{4})(\d{5})(\d{2})(\d{1})/, 'x-xxxx-xxxxx-$4-$5');
  }
  return id;
};

export const formatNationalIdNoMask = (id: string): string => {
  if (!id || IsEmptyValue(id)) {
    return '-';
  }

  const idN = (id.toString()).replace(/\D/g, '');
  if (idN.length === 13) {
    return idN.replace(/(\d{1})(\d{4})(\d{5})(\d{2})(\d{1})/, '$1-$2-$3-$4-$5');
  }
  return id;
};

export const formatSSOId = (id: string): string => {
  if (!id) {
    return '-';
  }

  const idN = (id.toString()).replace(/\D/g, '');
  if (idN.length === 13) {
    return formatNationalId(idN);
  } else if (idN.length === 10) {
    return idN.replace(/(\d{1})(\d{4})(\d{2})(\d{3})/, 'x-xxxx-xx$4');
  }
  return id;
};

export const formatDateValue = (date): string => {
  const nDate = new Date(date);
  if (!isNaN(nDate.getTime()) && nDate.getTime() > MinDateTime) {
    return formatDate(date, 'd MMM yyyy', 'en-US');
  }
  return null;
};

export const formatDateWithoutYearValue = (date): string => {
  return formatDate(date, 'd MMM', 'en-US');
};

export const formatTimeValue = (date): string => {
  return formatDate(date, 'HH:mm', 'en-US');
};

export const formatDateAndDuration = (date, endDate = null, allowSwap = false): string => {
  let val = formatDateValue(date);
  let diff = dateDiffDuration(date, endDate ? endDate : new Date(), allowSwap)
  let diffText = diff ? ` (${diff})` : ''
  return val ? `${val}${diffText}` : null;
};

export const dateDiffDuration = (startingDate, endingDate, allowSwap: boolean): string => {
  let t = new Date().toISOString().substr(0, 10) // need date in YYYY-MM-DD format
  let today = new Date((new Date(new Date(t).toUTCString()).setUTCHours(23, 59, 59, 999)));

  let startDate = new Date(new Date(new Date(startingDate).toUTCString()).toISOString().substr(0, 10));
  let endDate = today

  if (endingDate) {
    let d = new Date((new Date(new Date(endingDate).toUTCString()).setUTCHours(23, 59, 59, 999)));
    if (d < today) {
      endDate = d
    }
  }

  if (!allowSwap && startDate > endDate) {
    return null
  }

  if (startDate > endDate) {
    let swap = startDate;
    startDate = endDate;
    endDate = swap;
  }
  const startYear = startDate.getFullYear();
  const february = (startYear % 4 === 0 && startYear % 100 !== 0) || startYear % 400 === 0 ? 29 : 28;
  const daysInMonth = [31, february, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  let yearDiff = endDate.getFullYear() - startYear;
  let monthDiff = endDate.getMonth() - startDate.getMonth();
  if (monthDiff < 0) {
    yearDiff--;
    monthDiff += 12;
  }
  let dayDiff = endDate.getDate() - startDate.getDate();
  if (dayDiff < 0) {
    if (monthDiff > 0) {
      monthDiff--;
    } else {
      yearDiff--;
      monthDiff = 11;
    }
    dayDiff += daysInMonth[startDate.getMonth()];
  }

  let output = []
  if (yearDiff) output.push(`${yearDiff}y`) 
  if (monthDiff) output.push(`${monthDiff}m`) 
  if (dayDiff) output.push(`${dayDiff}d`) 
  return output.join(' ')
};

export const formatAmountBaht = (val, unit?): string => {
  const unitValue = unit ? ` /${unit}` : '';
  return val ? `฿${formatDecimalPrice(val)}${unitValue}` : '-';
};

export const formatNumber = (value) => {
  if (value || value === 0) {
    const n = value.toString();
    const p = n.indexOf('.');
    return n.replace(
      /\d(?=(?:\d{3})+(?:\.|$))/g,
      (m, i) => p < 0 || i < p ? `${m},` : m
    );
  }
  return '';
};

export const formatDateForPicker = (date: string) => {
  if (date) {
    const nDate = new Date(date);
    if (!isNaN(nDate.getTime()) && nDate.getTime() > MinDateTime) {
      return {
        year: nDate.getFullYear(),
        month: nDate.getMonth() + 1,
        day: nDate.getDate()
      };
    }
  }
  return null;
};

export const formatDatePickerTo = (date) => {
  if (date) {
    const nDate = new Date(date.year, date.month - 1, date.day, 7);
    if (!isNaN(nDate.getTime())) {
      return nDate.toISOString().slice(0, 10); // Show only date
    }
  }
  return null;
};

export function getDisplayYearTH(year: number): number {
  return year + 543;
}

export function getArrayDiff(a1: string[], a2: string[]): string[] {

  const a = [];
  const diff = [];

  for (let i = 0; i < a1.length; i++) {
    a[a1[i]] = true;
  }

  for (let i = 0; i < a2.length; i++) {
    if (a[a2[i]]) {
      delete a[a2[i]];
    } else {
      a[a2[i]] = true;
    }
  }

  for (const k in a) {
    diff.push(k);
  }

  return diff;
}

export function setIncomeLocalStorage(value: string[]): void {
  localStorage.setItem('incomeColumns', value.join('|||'));
}

export function setExpenditureLocalStorage(value: string[]): void {
  localStorage.setItem('expenditureColumns', value.join('|||'));
}

export function getIncomeLocalStorage(): string[] {
  return localStorage.getItem('incomeColumns')?.split('|||') || [];
}

export function getExpenditureLocalStorage(): string[] {
  return localStorage.getItem('expenditureColumns')?.split('|||') || [];
}

export function removeLocalColumnSelected(): void {
  localStorage.removeItem('incomeColumns');
  localStorage.removeItem('expenditureColumns');
}

export function setPayrollExpandListsLocalStorage(value): void {
  localStorage.setItem('payrollExpandLists', value ? JSON.stringify(value) : '');
}

export function getPayrollExpandListsLocalStorage() {
  try {
    const payrollExpandLists = localStorage.getItem('payrollExpandLists');
    if (payrollExpandLists) {
      return JSON.parse(payrollExpandLists)
    }
  } catch (e) {
    console.error('error getPayrollExpandListsLocalStorage:', e)
    return null;
  }
}

export function formatDecimalPrice(value: number): string {
  return IsEmptyValue(value) || value === 0 ? '0' : new Intl.NumberFormat('th-TH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value).replace('.00', '');
}

export function getTimeUnitInTH(unit: string): string {
  return unit === 'hourly' ? 'ชั่วโมง' : 'วัน';
}

export function getPrefixTimeUnit(value: string): string {
  return value.substring(0, value.indexOf('_') + 1);
}

export function numberWithoutCommas(x): number {
  x = IsEmptyValue(x) || x === '-' || x === '0' ? '0' : x.toString();
  return x === '0' ? 0 : parseFloat(x.replaceAll('\"', '').replace(/,/g, ''));
}

export function personalIdDisplayFormat(value: string): string {
  return value && !IsEmptyValue(value) && value.length === 13 ? value.replace(/^(\d{1})(\d{4})(\d{5})(\d{2})(\d{1}).*/, '$1-$2-$3-$4-$5') : '';
}

export function replaceUnicode(value): string {
  return value.replace('\u0000', '');
}

export function replaceRN(value): string {
  return value.replace('\r\n', '');
}

export function replaceStringMinus(value): string {
  return value.trim().replace('-', '');
}

export function displayCompanyNameWithBranch(name: string, branch: string): string {
  if (branch != null && branch != "00000") {
    return `${name} (${branch})`
  }
  return name
}

export const getNanoId = (digit: number = 10): string => {
  const nanoid = customAlphabet('0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ', digit);
  return nanoid(digit)
}

export function maskBahtIfZero(formattedNum: string): string {
  return formattedNum.replace(/฿0/g, '฿xxx')
}

export function formatPvdValue(pvdType: any, value: any, pvdPercentage: any): string {
  const pvdValue = formatAmountBaht(value, 'เดือน');
  if (pvdType === PvdTypePercent) {
    if (pvdPercentage != null) {
      let pvdStr = (pvdValue != '-') ? `(${pvdValue})` : ``
      return `${formatDecimalPrice(pvdPercentage)}% ${pvdStr}`
    } else {
      return '-';
    }
  } else {
    return pvdValue;
  }
}

export function GetMonthNameFromNum(month: number): string {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return months[month-1] || '';
}